import { useLoading } from '@/providers/LoadingProvider';
import { useEffect, useState } from 'react';
import { dateFormat } from '@/utils';
import { Link, useNavigate } from 'react-router-dom';
import { Pagination } from '@/components/element/Pagination';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { useBackofficeSearchCondition } from '@/providers/BackofficeSearchConditionProvider';
import { usePaymentTasks } from '@/hooks/api/0960_payment_tasks';
import { MotionDiv } from '@/components/element/MotionDiv';
import { FilterMatchMode } from 'primereact/api';
import { PaymentTask, PurchaseOrderContractTask } from '@/hooks/api/models';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useDataTableSortContent } from '@/hooks/useDataTableSortContent';

export const ListElement = () => {
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { _w_ } = useCurrentWorkspace();
  const { paymentTaskSearchCondition: searchCondition, setPaymentTaskSearchCondition: setSearchCondition } =
    useBackofficeSearchCondition();
  const {
    data: tasksRes,
    isPending,
    refetch,
  } = usePaymentTasks({
    ...searchCondition,
    per: 1000,
    identifier: _w_!.identifier,
  });
  const onPageLinkClick = (_page: number) => setSearchCondition((_) => ({ ...searchCondition, page: _page }));

  useEffect(() => {
    refetch();
  }, [searchCondition]);

  useEffect(() => setShowLoading(isPending), [isPending]);

  const [filters, setFilters] = useState({
    global: { value: '', matchMode: FilterMatchMode.CONTAINS },
  });

  const phaseTemplate = (t: PaymentTask) => t.payment_task_phase.label;

  const actualCostTemplate = (t: PaymentTask) => {
    if (t.business_partner_context.name == 'qualified') {
      return t.payment_amount_including_tax != null ? `¥${t.payment_amount_including_tax.toLocaleString()}` : '-';
    } else {
      return t.payment_amount != null ? `¥${t.payment_amount.toLocaleString()}` : '-';
    }
  };

  const assignTemplate = (t: PaymentTask) => (
    <div className="d-flex align-items-center">
      <img
        src={t.user.user_account.icon_file_url}
        className="avatar avatar-xs me-2 rounded-circle"
        alt="avatar image"
      />
      <h6 className="mb-0 text-xxs">{t.user.user_account.name}</h6>
    </div>
  );

  const dueDateTemplate = (t: PaymentTask) => (t.due_date ? dateFormat({ date: t.due_date }) : '-');

  // DataTableのソート、キーワード検索
  const { dataTableSortContent, onSort, onKeywordChanged } = useDataTableSortContent(`payment_task_${_w_!.id}`);

  // 検索ワードの設定
  useEffect(() => {
    let _filters = { ...filters };
    _filters.global.value = dataTableSortContent.dataTableSearchKeyword?.keyword || '';
    setFilters(_filters);
  }, [dataTableSortContent]);

  if (tasksRes == null) return <></>;

  return (
    <MotionDiv className="card mb-4">
      <div className="card-header pb-0 d-flex align-items-center justify-content-between">
        <div>
          <h5 className="mb-0">支払タスク一覧</h5>
        </div>
        <div>
          <div className="input-group">
            <span className="input-group-text">
              <i className="fas fa-search"></i>
            </span>
            <input
              className="form-control"
              placeholder="検索"
              type="text"
              value={filters.global.value}
              onChange={(e) => {
                const value = e.target.value;
                let _filters = { ...filters };
                _filters.global.value = value;
                setFilters(_filters);
                onKeywordChanged(value);
              }}
            />
          </div>
        </div>
      </div>
      <div className="card-body">
        {tasksRes.payment_tasks.length == 0 && (
          <p className="card-description text-sm">登録されている支払タスクがありません。</p>
        )}

        {tasksRes.payment_tasks.length > 0 && (
          <div className="table-responsive">
            <DataTable
              value={tasksRes.payment_tasks}
              stripedRows
              scrollable
              scrollHeight="800px"
              tableClassName="table data-table align-items-center clickable-row"
              removableSort
              onRowClick={(e) => {
                navigate(
                  `/w/${_w_!.identifier}/backoffice/teams/${e.data.purchase_order.team_outline.id}/purchase_orders/${
                    e.data.purchase_order.id
                  }/payment_tasks/${e.data.id}`
                );
              }}
              filters={filters}
              globalFilterFields={[
                'name',
                'purchase_order.name',
                'payment_schedule_detail.payment_schedule_outline.year_month',
                'user.user_account.name',
                'purchase_order.business_partner.display_name',
                'payment_task_phase.label',
                'purchase_order.team_outline.name',
              ]}
              emptyMessage="No Data."
              onSort={onSort}
              sortField={dataTableSortContent.dataTableSortField?.filed}
              sortOrder={dataTableSortContent.dataTableSortField?.sortOrder}
            >
              <Column
                header="発注先"
                field="purchase_order.business_partner.display_name"
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                headerStyle={{ backgroundColor: '#f9fafb' }}
                bodyStyle={{
                  backgroundColor: 'inherit',
                  minWidth: '100px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                }}
                frozen
                sortable
              ></Column>
              <Column
                header="タスク名"
                field="name"
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                headerStyle={{ backgroundColor: '#f9fafb' }}
                bodyStyle={{
                  backgroundColor: 'inherit',
                  minWidth: '250px',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                }}
                frozen
                sortable
              ></Column>
              <Column
                header="プロジェクト名"
                field="purchase_order.name"
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                style={{ minWidth: '50px' }}
                sortable
              ></Column>
              <Column
                header="請求月"
                field="payment_schedule_detail.payment_schedule_outline.year_month"
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                sortable
              ></Column>
              <Column
                header="フェーズ"
                field="payment_task_phase.id"
                body={phaseTemplate}
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                style={{ minWidth: '50px' }}
                sortable
              ></Column>
              <Column
                header="支払金額"
                field="payment_amount"
                body={actualCostTemplate}
                bodyClassName="text-xxs px-2 font-weight-bold text-end pe-4"
                headerClassName="text-uppercase text-xxs font-weight-bolder text-end"
                style={{ width: '50px' }}
                sortable
              ></Column>
              <Column
                header="タスク担当者"
                field="user.user_account.name"
                body={assignTemplate}
                bodyClassName="text-xxs px-2"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                sortable
              ></Column>
              <Column
                header="チーム名"
                field="purchase_order.team_outline.name"
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                sortable
              ></Column>
              <Column
                header="期限日"
                field="due_date"
                body={dueDateTemplate}
                bodyClassName="text-xxs px-2 font-weight-bold"
                headerClassName="text-uppercase text-xxs font-weight-bolder px-2"
                sortable
              ></Column>
            </DataTable>
          </div>
        )}
      </div>
    </MotionDiv>
  );
};
